export type UserRole = 'user' | 'admin'

export interface User {
  id: number
  email: string
  role: UserRole
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface SimulationResults {
  notaryFeeAmount: number
  pricePerM2: number
  investmentCost: number
  resaleEstimate: number
  monthlyLoanPayment: number
  totalInterest: number
  annualRent: number
  annualOperatingExpenses: number
  annualDebtService: number
  monthlyCashFlow: number
  annualCashFlow: number
  grossYield: number
  netYield: number
  financialYield: number
  grossMargin: number
  grossMarginPercent: number
  netMarginChargePercent: number
  capitalGainsTaxAmount: number
  netMarginBeforeTax: number
  netMargin: number
  roi: number
  annualizedReturn: number
  remainingLoanCapital: number
  interestDuringHolding: number
  earlyRepaymentIra: number
  resaleFeesDefault: number
}

export interface SimulationInput {
  name: string
  address: string
  surface: number
  purchasePrice: number
  compromis: number
  notaryFeeRate: number
  renovationCosts: number
  loanPrincipal: number
  loanRate: number
  loanDurationYears: number
  downPayment: number
  pricePerM2Resale: number
  holdingMonths: number
  resaleFees: number
  monthlyRent: number
  monthlyPropertyTax: number
  monthlyCondoFees: number
  monthlyInsurancePno: number
  monthlyManagementFee: number
}

export interface Simulation extends SimulationInput {
  id: number
  userId: number
  createdAt: string
  updatedAt: string
  results: SimulationResults
  project?: { id: number; status: ProjectStatus } | null
  role: ProjectRole
  isOwner: boolean
}

export type ProjectStatus = 'draft' | 'active' | 'on_hold' | 'done' | 'abandoned'

export type ProjectRole = 'owner' | 'reader' | 'collaborator'

export interface ProjectShare {
  id: number
  projectId: number
  userId: number
  email: string
  role: Exclude<ProjectRole, 'owner'>
  createdAt: string
}

export const projectRoleLabels: Record<ProjectRole, string> = {
  owner: 'Propriétaire',
  reader: 'Lecture seule',
  collaborator: 'Collaborateur',
}

export interface Project {
  id: number
  userId: number
  name: string
  status: ProjectStatus
  notes: string
  simulationId: number | null
  simulation?: Simulation
  createdAt: string
  updatedAt: string
  role: ProjectRole
  isOwner: boolean
}

export const emptySimulationInput: SimulationInput = {
  name: '',
  address: '',
  surface: 0,
  purchasePrice: 0,
  compromis: 0,
  notaryFeeRate: 7.5,
  renovationCosts: 0,
  loanPrincipal: 0,
  loanRate: 4,
  loanDurationYears: 20,
  downPayment: 0,
  pricePerM2Resale: 0,
  holdingMonths: 12,
  resaleFees: 0,
  monthlyRent: 0,
  monthlyPropertyTax: 0,
  monthlyCondoFees: 0,
  monthlyInsurancePno: 0,
  monthlyManagementFee: 0,
}

export function toSimulationInput(s: Simulation): SimulationInput {
  const out = {} as SimulationInput
  for (const key of Object.keys(emptySimulationInput) as (keyof SimulationInput)[]) {
    // @ts-expect-error indexed assignment across union of number/string keys
    out[key] = s[key]
  }
  return out
}

export const statusLabels: Record<ProjectStatus, string> = {
  draft: 'Brouillon',
  active: 'En cours',
  on_hold: 'En pause',
  done: 'Abouti',
  abandoned: 'Abandonné',
}
